import { useEffect, useMemo, useState } from "react";
import "./style.css";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import Spinner from "../../global-components/src/components/spinner/spiner";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../global-components/src";
import { useMessage } from "../../global-components/src";
import { errorMessage } from "../../global-components/src/utils/text";
import { PACOCA_API_URL } from "../../global-components/src/utils/vars";
import TermsUseText from "../../global-components/src/components/terms-use";
import GuidelinesText from "../../global-components/src/components/guidelines-text";
import { ChevronLeft } from "lucide-react";
import { sanitizeUsername, validateEmail, validateUsername } from "../../global-components/src/utils/auth";
import { isWeakPassword } from "../../global-components/src/utils/password-security";
import { User } from "../../global-components/src/types";
import { t } from "i18next";

const RegisterUser: React.FC = () => {
    const navigate = useNavigate();
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [passwordConfirmation, setPasswordConfirmation] = useState("");
    const [user_name, setUserName] = useState("");
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1);
    const lastStep = useMemo(() => 4, []);
    const location = useLocation();
    const [errorsInput, setErrorsInput] = useState<{
        name?: string[];
        user_name?: string[];
        acceptTerms?: string[];
        email?: string[];
        password?: string[];
    }>({});
    const [isVisible, setIsVisible] = useState(false);
    const [isVisibleConfirmation, setIsVisibleConfirmation] = useState(false);
    const [acceptTerms, setAcceptTerms] = useState(false);
    const { loginContext } = useAuth();
    const [usersSessions] = useState<User[]>([]);
    const { setMessage } = useMessage();

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const referral = params.get("referral_code");
        if (referral) sessionStorage.setItem("referral_code", referral);
    }, [location.search]);

    const verifyPasswordConfirmation = () => {
        let errors: Record<string, string[]> = {};
        if (password.length == passwordConfirmation.length && password != passwordConfirmation) {
            errors.password = [t("errors.input.passwordMismatch")];
        } else {
            delete errors.password;
        }
        setErrorsInput(errors);
    };

    useEffect(() => { verifyPasswordConfirmation(); }, [password, passwordConfirmation]);

    const togglePasswordVisibility = () => setIsVisible(!isVisible);
    const togglePasswordVisibilityConfirmation = () => setIsVisibleConfirmation(!isVisibleConfirmation);

    const handleRegister = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setLoading(true);
        setErrorsInput({});

        try {
            let errors: Record<string, string[]> = {};

            if (step == 1 || step == 0) {
                if (!name) errors.name = [t("errors.input.required", { field: t("inputs.name") })];
                if (!email) errors.email = [t("errors.input.required", { field: t("inputs.email") })];
                if (validateEmail(email)) errors.email = [validateEmail(email) ?? ""];
            }
            if (step == 2 || step == 0) {
                if (!user_name) errors.user_name = [t("errors.input.required", { field: t("inputs.username") })];
                else if (user_name.length < 3) errors.user_name = [t("errors.input.minLength", { field: t("inputs.username"), min: 3 })];
                else if (validateUsername(user_name) != null) errors.user_name = [validateUsername(user_name)!];
            }
            if (step == 3 || step == 0) {
                if (!password) errors.password = [t("errors.input.required", { field: t("inputs.password") })];
                else if (password.length < 8) errors.password = [t("errors.input.minLength", { field: t("inputs.password"), min: 8 })];
                else if (!/[a-zA-Z]/.test(password)) errors.password = [t("errors.input.invalid", { field: t("inputs.password") })];
                else if (isWeakPassword(password)) errors.password = [t("errors.input.passwordWeak")];
                else if (password !== passwordConfirmation) errors.password = [t("errors.input.passwordMismatch")];
            }
            if (step == 4 || step == 0) {
                if (!acceptTerms) errors.acceptTerms = [t("errors.input.required", { field: t("inputs.readTerms") })];
            }

            if (Object.keys(errors).length > 0) {
                setErrorsInput(errors);
                setLoading(false);
                return;
            }

            if (step > 0 && step < lastStep) {
                setLoading(false);
                setStep((prev) => prev + 1);
                return;
            }

            const referral_code = sessionStorage.getItem("referral_code");
            const response = await axios.post(`${PACOCA_API_URL}/register`, {
                name,
                password,
                user_name,
                email,
                password_confirmation: passwordConfirmation,
                terms: acceptTerms,
                referral_code: referral_code ?? null,
            });

            loginContext(response.data.access_token, response.data.user, acceptTerms);
            sessionStorage.removeItem("referral_code");
            navigate("/");
            setMessage(
                referral_code
                    ? t("register.verifyEmailWithReferral")
                    : t("register.verifyEmail"),
                "success",
                response.data.message
            );
        } catch (err: any) {
            setStep(0);
            if (err?.response?.data.errors) {
                setErrorsInput(err.response.data.errors);
            } else {
                const type = err?.response?.data?.type_message || "error";
                const messageError = errorMessage(err);
                setMessage(messageError, type);
            }
        }
        setLoading(false);
    };

    const onChangeUsername = (value: string) => setUserName(sanitizeUsername(value));

    const stepLabels = [
        t("register.steps.personalData"),
        t("register.steps.username"),
        t("register.steps.password"),
        t("register.steps.terms"),
    ];
    const eyeOpen = (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
        </svg>
    );
    const eyeOff = (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
            <line x1="1" y1="1" x2="23" y2="23"/>
        </svg>
    );

    return (
        <div className="auth-root">
            <Helmet>
                <title>{`Paçoca - ${t("register.title")}`}</title>
                <meta name="description" content={t("register.metaDescription")} />
                <meta name="keywords" content={t("register.metaKeywords")} />
                <meta name="author" content="Paçoca Inc." />
                <meta property="og:title" content={`Paçoca - ${t("register.metaTitle")}`} />
                <meta property="og:description" content={t("register.metaDescription")} />
                <meta property="og:image" content="/img/link-preview.png?v=1" />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:url" content={window.location.href} />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={`Paçoca - ${t("register.metaTitle")}`} />
                <meta name="twitter:description" content={t("register.metaDescription")} />
                <meta name="twitter:image" content="/img/link-preview.png?v=1" />
            </Helmet>

            <div className="auth-layout auth-layout--register">
                {/* ── Left panel ── */}
                <div className="auth-panel-left" aria-hidden="true">
                    <div className="auth-orb auth-orb-1" />
                    <div className="auth-orb auth-orb-2" />
                    <div className="auth-orb auth-orb-3" />
                    <div className="auth-grid" />
                    <div className="auth-panel-left__content">
                        <div className="auth-badge">
                            <span className="auth-badge__dot" />
                            {t("register.brazilianSocialNetwork")}
                        </div>
                        <h2 className="auth-panel-left__headline">
                            {t("register.joinCommunity")}<br />
                            <span>{t("register.ourCommunity")}</span>
                        </h2>
                        <p className="auth-panel-left__sub">
                            {t("register.communityDescription")}
                        </p>
                        {/* Step progress indicators */}
                        <div className="auth-step-indicators">
                            {[1, 2, 3, 4].map((s) => (
                                <div
                                    key={s}
                                    className={`auth-step-indicator ${step >= s || step === 0 ? "auth-step-indicator--done" : ""} ${step === s ? "auth-step-indicator--active" : ""}`}
                                >
                                    <span className="auth-step-indicator__num">{s}</span>
                                    <span className="auth-step-indicator__label">{stepLabels[s - 1]}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ── Right panel ── */}
                <div className="auth-panel-right">
                    <div className="auth-logo">
                        <img src="/img/pacoca-sem-braco.png" alt="Paçoca" className="auth-logo__img" />
                        <span className="auth-logo__name">Paçoca</span>
                    </div>

                    <form className="auth-form" id="form-login" onSubmit={handleRegister}>
                        {step > 1 && (
                            <button
                                type="button"
                                className="auth-back-btn"
                                onClick={() => setStep((prev) => (prev <= 0 ? 1 : prev - 1))}
                                aria-label={t("register.back")}
                            >
                                <ChevronLeft size={18} />
                                {t("register.back")}
                            </button>
                        )}

                        <h1 className="auth-title">{t("register.title")}</h1>
                        <p className="auth-subtitle">
                            {step === 1 && t("register.stepSubtitles.personalData")}
                            {step === 2 && t("register.stepSubtitles.username")}
                            {step === 3 && t("register.stepSubtitles.password")}
                            {step === 4 && t("register.stepSubtitles.terms")}
                        </p>

                        {/* Step progress bar (mobile) */}
                        <div className="auth-progress">
                            <div
                                className="auth-progress__bar"
                                style={{ width: `${(step / lastStep) * 100}%` }}
                            />
                        </div>

                        {/* ── Step 1: Name + Email ── */}
                        {(step === 1 || step === 0) && (
                            <>
                                <div className="auth-field">
                                    <label className="auth-field__label" htmlFor="reg-name">
                                        {t("inputs.name")}
                                    </label>
                                    <div className="auth-field__wrap">
                                        <span className="auth-field__icon">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                                        </span>
                                        <input
                                            id="reg-name"
                                            type="text"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            className={`auth-input ${errorsInput.name ? "auth-input--error" : ""}`}
                                            name="name"
                                            placeholder={t("inputs.name")}
                                            autoComplete="name"
                                        />
                                    </div>
                                    {errorsInput.name && <span className="auth-error">{errorsInput.name[0]}</span>}
                                </div>

                                <div className="auth-field">
                                    <label className="auth-field__label" htmlFor="reg-email">
                                        {t("inputs.email")}
                                    </label>
                                    <div className="auth-field__wrap">
                                        <span className="auth-field__icon">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                                        </span>
                                        <input
                                            id="reg-email"
                                            type="text"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className={`auth-input ${errorsInput.email ? "auth-input--error" : ""}`}
                                            name="email"
                                            placeholder={t("inputs.email")}
                                            autoComplete="email"
                                        />
                                    </div>
                                    {errorsInput.email && <span className="auth-error">{errorsInput.email[0]}</span>}
                                </div>
                            </>
                        )}

                        {/* ── Step 2: Username ── */}
                        {(step === 2 || step === 0) && (
                            <div className="auth-field">
                                <label className="auth-field__label" htmlFor="reg-username">
                                    {t("inputs.username")}
                                </label>
                                <div className="auth-field__wrap">
                                    <span className="auth-field__icon auth-field__icon--text">@</span>
                                    <input
                                        autoCapitalize="none"
                                        id="reg-username"
                                        type="text"
                                        value={user_name}
                                        onChange={(e) => onChangeUsername(e.target.value)}
                                        className={`auth-input auth-input--at ${errorsInput.user_name ? "auth-input--error" : ""}`}
                                        name="user_name"
                                        placeholder={t("inputs.username")}
                                        autoComplete="username"
                                    />
                                </div>
                                {errorsInput.user_name && (
                                    <span className="auth-error">{errorsInput.user_name[0]}</span>
                                )}
                                <span className="auth-hint">{t("inputs.usernameHelp")}</span>
                            </div>
                        )}

                        {/* ── Step 3: Password ── */}
                        {(step === 3 || step === 0) && (
                            <>
                                <div className="auth-field">
                                    <label className="auth-field__label" htmlFor="reg-password">
                                        {t("inputs.password")}
                                    </label>
                                    <div className="auth-field__wrap">
                                        <span className="auth-field__icon">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                                        </span>
                                        <input
                                            id="reg-password"
                                            type={isVisible ? "text" : "password"}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className={`auth-input ${errorsInput.password ? "auth-input--error" : ""}`}
                                            name="password"
                                            placeholder={t("inputs.password")}
                                            autoComplete="new-password"
                                        />
                                        <button
                                            type="button"
                                            className="auth-field__eye"
                                            onClick={togglePasswordVisibility}
                                            aria-label={t("register.showPassword")}
                                        >
                                            {isVisible ? eyeOff : eyeOpen}
                                        </button>
                                    </div>
                                    {errorsInput.password && (
                                        <span className="auth-error">{errorsInput.password[0]}</span>
                                    )}
                                    <span className="auth-hint">{t("inputs.passwordHelp")}</span>
                                    <span className="auth-hint">{t("inputs.passwordHelp2")}</span>
                                    <span className="auth-hint">{t("inputs.passwordSecurityHelp")}</span>
                                </div>

                                <div className="auth-field">
                                    <label className="auth-field__label" htmlFor="reg-password-confirm">
                                        {t("inputs.passwordConfirmation")}
                                    </label>
                                    <div className="auth-field__wrap">
                                        <span className="auth-field__icon">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                                        </span>
                                        <input
                                            id="reg-password-confirm"
                                            type={isVisibleConfirmation ? "text" : "password"}
                                            value={passwordConfirmation}
                                            onChange={(e) => setPasswordConfirmation(e.target.value)}
                                            className="auth-input"
                                            name="password_confirmation"
                                            placeholder={t("inputs.passwordConfirmation")}
                                            autoComplete="new-password"
                                        />
                                        <button
                                            type="button"
                                            className="auth-field__eye"
                                            onClick={togglePasswordVisibilityConfirmation}
                                            aria-label={t("register.showPasswordConfirmation")}
                                        >
                                            {isVisibleConfirmation ? eyeOff : eyeOpen}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* ── Step 4: Terms ── */}
                        {(step === 4 || step === 0) && (
                            <div className="auth-field">
                                <h5 className="auth-field__label" style={{ marginBottom: 12 }}>
                                    {t("inputs.readTerms")}
                                </h5>
                                <label className="auth-checkbox auth-checkbox--terms">
                                    <input
                                        type="checkbox"
                                        checked={acceptTerms}
                                        onChange={() => setAcceptTerms(!acceptTerms)}
                                        className="auth-checkbox__input"
                                        name="termos"
                                        id="termos"
                                    />
                                    <span className="auth-checkbox__box" />
                                    <span className="auth-checkbox__label">
                                        {t("inputs.acceptTerms")}{" "}
                                        <button
                                            type="button"
                                            className="auth-link auth-link--inline"
                                            data-bs-toggle="modal"
                                            data-bs-target="#modal-termos-de-uso"
                                        >
                                            {t("inputs.terms")}
                                        </button>
                                        {" "}{t("inputs.and")}{" "}
                                        <button
                                            type="button"
                                            className="auth-link auth-link--inline"
                                            data-bs-toggle="modal"
                                            data-bs-target="#modal-politicas-de-privacidade"
                                        >
                                            {t("inputs.guidelines")}
                                        </button>
                                        {" "}{t("inputs.ofPacoca")}
                                    </span>
                                </label>
                                {errorsInput.acceptTerms && (
                                    <span className="auth-error">{errorsInput.acceptTerms[0]}</span>
                                )}
                            </div>
                        )}

                        {/* Submit */}
                        <button type="submit" className="auth-btn-primary" disabled={loading}>
                            {loading ? <Spinner /> : (
                                <span>
                                    {step === lastStep || step === 0
                                        ? t("register.createAccount")
                                        : t("generics.next")}
                                </span>
                            )}
                        </button>

                        <div className="auth-center-link" style={{ marginTop: 16 }}>
                            <Link className="auth-link" to="/login">{t("register.login")}</Link>
                        </div>
                    </form>
                </div>
            </div>

            {/* ── Modal: Termos de uso ── */}
            <div className="modal fade" style={{ overflow: "hidden" }} id="modal-termos-de-uso" tabIndex={-1} aria-labelledby="modal-termos-de-uso" aria-hidden="true">
                <div className="modal-dialog cover-mobile">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{t("register.termsModalTitle")}</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label={t("register.close")} />
                        </div>
                        <div className="modal-body" style={{ maxHeight: "80vh", overflow: "auto" }}>
                            <TermsUseText />
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">{t("register.close")}</button>
                            <button type="button" data-bs-dismiss="modal" className="btn btn-blue" onClick={() => setAcceptTerms(true)}>{t("register.accept")}</button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Modal: Diretrizes ── */}
            <div className="modal fade" style={{ overflow: "hidden" }} id="modal-politicas-de-privacidade" tabIndex={-1} aria-labelledby="modal-politicas-de-privacidade" aria-hidden="true">
                <div className="modal-dialog cover-mobile">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{t("register.guidelinesModalTitle")}</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label={t("register.close")} />
                        </div>
                        <div className="modal-body" style={{ maxHeight: "80vh", overflow: "auto" }}>
                            <GuidelinesText />
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">{t("register.close")}</button>
                            <button type="button" data-bs-dismiss="modal" className="btn btn-blue" onClick={() => setAcceptTerms(true)}>{t("register.accept")}</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RegisterUser;